import { Plus } from 'lucide-react';

import { Button } from 'shadcn/components/ui/Button';
import { Skeleton } from 'shadcn/components/ui/Skeleton';

import BannerEditor from './BannerEditor';
import CarouselEditor from './CarouselEditor';

/**
 * Displays a section for an aisle.
 *
 * @param {Object} aisle - Aisle data.
 * @param {Object} aisleRefs - Reference to the aisle elements (used to observe the scroll positions)
 * @param {Array} banners - Aisle banner data.
 * @param {Object} carousels - Carousel data.
 * @param {Function} handleDeleteNewEditor - Callback function to delete a new carousel editor.
 * @param {Array} newBannerEditors - Editors for banners not yet saved.
 * @param {Object} newCarouselEditors - Editors for carousels not yet saved.
 * @param {Function} refetchBanners - Function to refetch banners from the parent component.
 * @param {Function} refetchCarousels - Function to refetch carousels from the parent component.
 * @param {Array} sellableItems - Sellable items.
 * @param {Array} sortedAisles - The list of aisles to duplicate to.
 * @param {Function} setDialogAisleID - Callback function to set the dialog aisle ID.
 */
export default function AisleContentSection({
    aisle,
    aisleRefs,
    banners,
    carousels,
    handleDeleteNewEditor,
    newBannerEditors,
    newCarouselEditors,
    refetchBanners,
    refetchCarousels,
    sellableItems,
    setDialogAisleID,
    sortedAisles,
}) {
    return (
        <li
            id={`aisle-content-section-${aisle.id}`}
            ref={(el) => {
                // eslint-disable-next-line no-param-reassign
                if (el) aisleRefs.current[aisle.id] = el;
            }}
            data-aisle-id={aisle.id}
            data-aisle-level={aisle.level}
            className="flex flex-col items-start gap-4 w-full scroll-mt-42"
        >
            <div className="flex items-center justify-between w-full">
                <h2 className="text-xl font-bold">{aisle.name}</h2>
                <Button variant="outline" onClick={() => setDialogAisleID(aisle.id)}>
                    Add Content <Plus />
                </Button>
            </div>

            <div className="flex flex-col gap-4 w-full">
                {newBannerEditors.map((editor, index) => (
                    <BannerEditor
                        key={editor.id}
                        aisleID={aisle.id}
                        banner={editor.variant.defaultData}
                        initialView="form"
                        handleDeleteNewEditor={() =>
                            handleDeleteNewEditor({ aisleID: aisle.id, editorIndex: index, editorType: 'banner' })
                        }
                        refetchBanners={refetchBanners}
                    />
                ))}
                {newCarouselEditors.map((editor, index) => (
                    <CarouselEditor
                        key={editor.id}
                        aisleID={aisle.id}
                        aisleLevel={aisle.level}
                        carousel={editor.variant.defaultData}
                        initialView="form"
                        handleDeleteNewEditor={() =>
                            handleDeleteNewEditor({ aisleID: aisle.id, editorIndex: index, editorType: 'carousel' })
                        }
                        refetchCarousels={refetchCarousels}
                        sellableItems={sellableItems}
                        sortedAisles={sortedAisles}
                    />
                ))}
                {banners.map((banner) => (
                    <BannerEditor key={banner.id} aisleID={aisle.id} banner={banner} refetchBanners={refetchBanners} />
                ))}
                {carousels.map((carousel) => (
                    <CarouselEditor
                        key={carousel.id}
                        aisleID={aisle.id}
                        aisleLevel={aisle.level}
                        carousel={carousel}
                        refetchCarousels={refetchCarousels}
                        sellableItems={sellableItems}
                        sortedAisles={sortedAisles}
                    />
                ))}
            </div>

            {!banners.length && !carousels.length && !newBannerEditors.length && !newCarouselEditors.length && (
                <div className="w-full p-16 text-muted-foreground italic border border-dashed rounded-lg bg-muted">
                    No content
                </div>
            )}
        </li>
    );
}

/**
 * Loading skeleton for the aisle content section.
 */
export function AisleContentSectionSkeleton() {
    return (
        <li className="flex flex-col items-start gap-4 w-full scroll-m-42">
            <div className="flex items-center justify-between w-full">
                <Skeleton className="w-50 h-10" />
                <Skeleton className="w-35 h-10" />
            </div>
            <div className="flex flex-col gap-4 w-full">
                <Skeleton className="w-full h-118" />
            </div>
        </li>
    );
}
